import type { LineChartProps, RenderTooltipContentData } from '@shopify/polaris-viz';
import type { IReportTable } from '../../common/Visualization/types';
import type { IRMetricKey } from '../../common/type';

import { PageTooltip, SingleSeriesTooltip, VersionTooltip } from '../components/common';
import { getFormattedByInterval, useMetricsTimeFilter } from '~/modules/analytics';
import { useMemo, useCallback, useState } from 'react';
import { REPORT_METRIC_INFO } from '../../common';
import { useChartData } from './useChartData';
import { getLineChartData } from '../utils';

export interface LineChartDataProps {
  metricKey: IRMetricKey;
  data: IReportTable;
  dimensions: string[];
}

export const useLineChartData = ({ metricKey, data, dimensions }: LineChartDataProps) => {
  const { currentDates, previousDates } = useMetricsTimeFilter();

  const {
    seriesNameFormatter,
    yAxisOptions,
    interval,
    formatValue,
    timeDimension,
    templateLabelById,
    onlyHasTimeDimension,
    formatKeyByInterval,
  } = useChartData({
    data,
    dimensions,
    metricKey,
  });

  const [activeSeriesKey, setActiveSeriesKey] = useState<number>();

  const {
    seriesData: lineChartData,
    seriesKey,
    hasCompareData,
  } = useMemo(() => {
    return getLineChartData({
      data,
      metricKey,
      dimensions,
      timeDimension,
      onlyHasTimeDimension,
      templateLabelById,
      dateFilter: { currentDates, previousDates },
    });
  }, [
    metricKey,
    data,
    dimensions,
    timeDimension,
    onlyHasTimeDimension,
    templateLabelById,
    currentDates,
    previousDates,
  ]);

  const renderTooltipContent = useCallback(
    (data: RenderTooltipContentData) => {
      if (onlyHasTimeDimension)
        return (
          <PageTooltip
            data={data}
            formatValue={formatValue}
            formatKeyByInterval={formatKeyByInterval}
            metricKey={metricKey}
          />
        );

      const hoverAllSeries = activeSeriesKey === -1;
      if (hoverAllSeries || !hasCompareData) {
        return (
          <VersionTooltip
            data={data}
            seriesKey={seriesKey}
            formatValue={formatValue}
            metricKey={metricKey}
          />
        );
      }

      return (
        <SingleSeriesTooltip
          data={data}
          activeSeriesKey={activeSeriesKey}
          formatKeyByInterval={formatKeyByInterval}
          title={REPORT_METRIC_INFO[metricKey].title}
          formatValue={formatValue}
          metricKey={metricKey}
        />
      );
    },
    [
      seriesKey,
      onlyHasTimeDimension,
      formatValue,
      formatKeyByInterval,
      activeSeriesKey,
      hasCompareData,
      metricKey,
    ],
  );

  const xAxisOptions: LineChartProps['xAxisOptions'] = useMemo(() => {
    return {
      labelFormatter: (value) => {
        return formatKeyByInterval(value);
      },
    };
  }, [formatKeyByInterval]);

  const tooltipOptions: LineChartProps['tooltipOptions'] = useMemo(() => {
    return {
      titleFormatter: (value) => {
        if (onlyHasTimeDimension) return REPORT_METRIC_INFO[metricKey].title;

        return getFormattedByInterval(value, interval, {
          isExpandDetail: true,
        });
      },
      valueFormatter: formatValue,
      renderTooltipContent,
    };
  }, [interval, formatValue, renderTooltipContent, onlyHasTimeDimension, metricKey]);

  return {
    lineChartData,
    seriesNameFormatter,
    xAxisOptions,
    yAxisOptions,
    tooltipOptions,
    seriesKey,
    onlyHasTimeDimension,
    timeDimension,
    setActiveSeriesKey,
  };
};
